import { useEffect, useState, useCallback } from 'react';
import db from '../db';
import { useNotifications } from '../contexts/NotificationContext';

/**
 * Hook to fetch new self-registered members for the current OPD and add them to notifications
 * This ensures OPD admins see notifications when new members register themselves
 */
export const useSelfRegisteredNotifications = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const { addNotification } = useNotifications();

  // Helper function to get processed member IDs from localStorage
  const getProcessedMembers = useCallback(() => {
    try {
      const stored = localStorage.getItem('self_registered_notifications_processed');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading processed self-registered notifications:', error);
      return [];
    }
  }, []);

  // Helper function to mark members as processed
  const markMembersAsProcessed = useCallback((memberIds) => {
    try {
      const processed = getProcessedMembers();
      const updated = [...new Set([...processed, ...memberIds])];
      localStorage.setItem('self_registered_notifications_processed', JSON.stringify(updated));
    } catch (error) {
      console.error('Error storing processed self-registered notifications:', error);
    }
  }, [getProcessedMembers]);

  // Function to fetch new self-registered members for current OPD
  const fetchNewSelfRegisteredMembers = useCallback(async () => {
    if (isLoading || hasFetched) return;

    try {
      setIsLoading(true);

      // Get current user to filter by OPD
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (!user || !user.opdId) {
        setHasFetched(true);
        return;
      }

      // Fetch self-registered members for this OPD
      const selfRegisteredMembers = await db.selfRegisteredMembers
        .where('opdId')
        .equals(user.opdId)
        .toArray();

      // Get already processed members to avoid duplicates
      const processedMembers = getProcessedMembers();

      // Filter for new members that haven't been processed yet
      const newMembers = selfRegisteredMembers.filter(member =>
        !processedMembers.includes(member.id)
      );

      if (newMembers.length > 0) {
        newMembers.forEach((member) => {
          addNotification({
            type: 'self-registered',
            title: 'New Self-Registered Member',
            message: `${member.fullName} has registered as a member of your OPD`,
            data: {
              memberId: member.id,
              memberName: member.fullName,
              disabilityType: member.disabilityType,
              phoneNumber: member.phoneNumber
            }
          });
        });

        // Mark these members as processed
        markMembersAsProcessed(newMembers.map(m => m.id));
      }

      setHasFetched(true);
    } catch (error) {
      console.error('Error fetching self-registered members for notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasFetched, addNotification, getProcessedMembers, markMembersAsProcessed]);

  // Reset fetch state when needed
  const resetFetchState = useCallback(() => {
    setHasFetched(false);
  }, []);
  return {
    fetchNewSelfRegisteredMembers,
    resetFetchState,
    isLoading,
    hasFetched
  };
};
